import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const NAV_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/expertise", label: "Expertise" },
  { to: "/about", label: "About" },
];

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const onDarkHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-paper text-gray-900">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-gold focus:px-4 focus:py-2 focus:text-ink"
      >
        Skip to content
      </a>

      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          onDarkHero
            ? "border-b border-transparent bg-transparent"
            : "border-b border-ink/10 bg-paper/90 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
          <Link to="/" aria-label="W Group Consulting — home" data-testid="nav-home-link">
            <Logo light={onDarkHero} />
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-300 ${
                    onDarkHero
                      ? "text-paper/80 hover:text-paper"
                      : "text-ink/70 hover:text-ink"
                  } ${isActive ? "underline decoration-gold decoration-2 underline-offset-8" : ""}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className={`border px-5 py-2.5 text-sm tracking-wide transition-colors duration-300 ${
                onDarkHero
                  ? "border-gold text-gold hover:bg-gold hover:text-ink"
                  : "border-ink text-ink hover:bg-ink hover:text-paper"
              }`}
            >
              Start a conversation
            </Link>
          </nav>

          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden ${onDarkHero ? "text-paper" : "text-ink"}`}
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              aria-label="Mobile"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-b border-ink/10 bg-paper lg:hidden"
            >
              <div className="flex flex-col gap-1 px-6 py-6">
                {[...NAV_LINKS, { to: "/contact", label: "Contact" }].map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                    className={({ isActive }) =>
                      `py-3 font-display text-2xl text-ink transition-colors ${
                        isActive ? "italic text-gold" : ""
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main id="main-content">{children}</main>

      <footer className="bg-ink text-paper" data-testid="site-footer">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
            <div>
              <Logo light />
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-paper/60">
                Business consultancy and advisory support for owners and
                leadership teams — at home and across new jurisdictions.
              </p>
            </div>
            <nav aria-label="Footer" className="flex flex-col gap-3 text-sm">
              <span className="mb-2 text-xs uppercase tracking-[0.2em] text-gold">Explore</span>
              {[...NAV_LINKS, { to: "/contact", label: "Contact" }].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(" ", "-")}`}
                  className="text-paper/70 transition-colors hover:text-paper"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <nav aria-label="Legal" className="flex flex-col gap-3 text-sm">
              <span className="mb-2 text-xs uppercase tracking-[0.2em] text-gold">Legal</span>
              <Link to="/privacy" data-testid="footer-link-privacy" className="text-paper/70 transition-colors hover:text-paper">
                Privacy Policy
              </Link>
              <Link to="/terms" data-testid="footer-link-terms" className="text-paper/70 transition-colors hover:text-paper">
                Terms of Use
              </Link>
            </nav>
          </div>
          <div className="mt-16 flex flex-col gap-3 border-t border-paper/10 pt-8 text-xs text-paper/40 md:flex-row md:items-center md:justify-between">
            <span>© {new Date().getFullYear()} W Group Consulting. All rights reserved.</span>
            <a
              href="mailto:hello@wgroupconsulting.co.uk"
              data-testid="footer-email-link"
              className="transition-colors hover:text-paper/70"
            >
              hello@wgroupconsulting.co.uk
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
